import axios, {AxiosResponse} from 'axios';
import React, {Fragment, ReactElement, useState} from 'react';
import {ServiceErrorMessage} from '@mo/service-error-message';
import './embedded-dealer-code-change.scss';
import cn from 'classnames';

import DealerSelectionDropdown from './DealerSelectionDropdown';
import DealerSelectionList from './DealerSelectionList';

function displayAddress({line1, line2, line3, line4, postcode}: Address): string {
    return [line1, line2, line3, line4, postcode].filter((attribute) => !!attribute).join(', ');
}

async function responseOrNull<T>(axiosCall: () => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T> | null> {
    try {
        const response = await axiosCall();
        if (response.status !== 200) {
            throw new Error('This is a problem');
        }
        return response;
    } catch (e) {
        return null;
    }
}

export function convertDealerToDealerItem(dealer: Dealer): DealerItem {
    return {
        description: `${dealer.name} - ${dealer.code}, ${dealer.address.postcode}`,
        code: dealer.code,
        name: dealer.name,
        address: displayAddress(dealer.address),
        status: dealer.status
    };
}

function EmbeddedDealerCodeChange(props: {
    loading?: boolean;
    error?: boolean;
    baseUrl: string;
    dealers?: Dealer[];
    onSuccessfulSubmission: (dealerCode?: string) => void;
    onSelection?: (dealerCode: string) => void;
    role: 'card' | 'menu-item';
    styles?: {
        dropdownClassname?: string;
        listClassname?: string;
        errorClassname?: string;
    };
}): ReactElement {
    const [selectionError, setSelectionError] = useState(false);

    const dealers: DealerItem[] = props.dealers ? props.dealers.map(convertDealerToDealerItem) : [];

    const changeDealerAndReload = async (dealerCode: string): Promise<void> => {
        setSelectionError(false);

        const response = await responseOrNull(() => axios.post(`${props.baseUrl}/auth/changeDealer`, {dealerCode}));
        if (response === null) return setSelectionError(true);

        props.onSuccessfulSubmission(dealerCode);
    };

    const shouldDisplayDropdown = () => dealers.length > 10

    return props.error ? (
        <ServiceErrorMessage
            className={cn('embedded-dealer-code-change__service-error', {
                'embedded-dealer-code-change__service-error__card': props.role === 'card',
            }, props.styles?.errorClassname)}
            visible={props.error}
            title='Our fault - a technical error happened'
        >
            <>
                We had trouble getting the list of dealerships you have access to. Please refresh the page to try again.
            </>
        </ServiceErrorMessage>
    ) : (
        <Fragment>
            {(props.loading || shouldDisplayDropdown()) && (
                <DealerSelectionDropdown
                    role={props.role}
                    dealers={dealers}
                    onSelection={(dealerCode: string): Promise<void> | void => {
                        props.onSelection ? props.onSelection(dealerCode) : changeDealerAndReload(dealerCode)
                    }}
                    minCharacters={dealers.length > 1000 ? 2 : 1}
                    classname={props.styles?.dropdownClassname}
                />
            )}
            {!shouldDisplayDropdown() && ((props.role === 'card' && dealers.length >= 1) || (props.role === 'menu-item' && dealers.length > 1)) ? (
                <DealerSelectionList
                    role={props.role}
                    dealers={dealers}
                    onClick={(dealerCode: string): Promise<void> | void => {
                        props.onSelection ? props.onSelection(dealerCode) : changeDealerAndReload(dealerCode)
                    }}
                    classname={props.styles?.listClassname}
                />
            ) : null}
            <ServiceErrorMessage
                className={cn('embedded-dealer-code-change__service-error', {
                    'embedded-dealer-code-change__service-error__card': props.role === 'card',
                }, props.styles?.errorClassname)}
                visible={selectionError}
                title='Our fault - a technical error happened'
            >
                <>
                    Sorry, we’re having some problems loading this dealer code. Please refresh the page and try again in
                    a few moments. If this problem persists please call us on{' '}
                    <a href={'tel:0300 037 0200'}>0300 037 0200</a>.
                </>
            </ServiceErrorMessage>
        </Fragment>
    );
}

export interface Address {
    line1: string;
    line2: string;
    line3: string;
    line4: string;
    postcode: string;
}

export interface Dealer {
    code: string;
    name: string;
    address: Address;
    status: 'Open' | 'Closed' | 'Closing Down';
}

export interface DealerItem {
    description: string;
    code: string;
    name: string;
    address: string;
    status: 'Open' | 'Closed' | 'Closing Down';
}

export default EmbeddedDealerCodeChange;
